/**
 * MatchFilters — URL-synced filter bar for the matches list.
 *
 * Filters available:
 *   - status: presented / mutual / connected / unmatched / expired
 *   - origin: organic / tester_instant / fake_paired
 *   - flagged: true / "" (boolean toggle)
 *   - search: free-text on user1/user2 email + display_name
 *
 * The component is controlled — it never owns state directly. Parent
 * pushes the current `filters` object in and gets the new object out
 * via `onChange`, so the parent can sync to the URL with
 * `setSearchParams`.
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-matches-summaries-design.md §7.1
 */

interface Props {
  filters: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}

export function MatchFilters({ filters, onChange }: Props) {
  // Drop empty-string keys so the URL stays tidy when a filter is cleared.
  const update = (patch: Record<string, string>) => {
    const next: Record<string, string> = { ...filters, ...patch };
    for (const key of Object.keys(next)) {
      if (next[key] === '' || next[key] == null) delete next[key];
    }
    onChange(next);
  };

  return (
    <div
      className="mt-2 flex flex-wrap gap-2 rounded-md border bg-muted/50 p-3"
    >
      <select
        value={filters.status || ''}
        onChange={e => update({ status: e.target.value })}
        className={controlClass}
        aria-label="Filter by status"
      >
        <option value="">All statuses</option>
        <option value="presented">Presented</option>
        <option value="mutual">Mutual</option>
        <option value="connected">Connected</option>
        <option value="unmatched">Unmatched</option>
        <option value="expired">Expired</option>
      </select>

      <select
        value={filters.origin || ''}
        onChange={e => update({ origin: e.target.value })}
        className={controlClass}
        aria-label="Filter by origin"
      >
        <option value="">All origins</option>
        <option value="organic">Organic</option>
        <option value="tester_instant">Tester instant</option>
        <option value="fake_paired">Fake-paired</option>
      </select>

      <select
        value={filters.participant || ''}
        onChange={e => update({ participant: e.target.value })}
        className={controlClass}
        aria-label="Filter by participant type"
        title="Filter by who is in the match (either participant). Origin is how the match was created; this is the participant's identity type."
      >
        <option value="">All participants</option>
        <option value="real">Real only</option>
        <option value="fake">Involves fake</option>
        <option value="fake_tailored">Fake-tailored</option>
        <option value="fake_seeded">Fake-seeded</option>
        <option value="tester">Involves tester</option>
      </select>

      <select
        value={filters.summary_status || ''}
        onChange={e => update({ summary_status: e.target.value })}
        className={controlClass}
        aria-label="Filter by summary coverage"
        title="Whether the match has a generated AI compatibility summary."
      >
        <option value="">Any summary</option>
        <option value="ready">Has summary</option>
        <option value="missing">Missing summary</option>
      </select>

      <input
        type="number"
        placeholder="Min score"
        value={filters.score_min || ''}
        onChange={e => update({ score_min: e.target.value })}
        className={controlClass}
        style={{ width: 100 }}
        aria-label="Minimum compatibility score"
      />
      <input
        type="number"
        placeholder="Max score"
        value={filters.score_max || ''}
        onChange={e => update({ score_max: e.target.value })}
        className={controlClass}
        style={{ width: 100 }}
        aria-label="Maximum compatibility score"
      />

      <input
        type="date"
        value={filters.date_from || ''}
        onChange={e => update({ date_from: e.target.value })}
        className={controlClass}
        aria-label="Matched on or after"
      />
      <input
        type="date"
        value={filters.date_to || ''}
        onChange={e => update({ date_to: e.target.value })}
        className={controlClass}
        aria-label="Matched on or before"
      />

      <label className="flex items-center gap-1 text-sm">
        <input
          type="checkbox"
          checked={filters.flagged === 'true'}
          onChange={e => update({ flagged: e.target.checked ? 'true' : '' })}
        />
        Flagged only
      </label>
      <select
        value={filters.flag_source || ''}
        onChange={e => update({ flag_source: e.target.value })}
        className={controlClass}
        aria-label="Filter by flag source"
      >
        <option value="">All flag sources</option>
        <option value="safety_rail">Safety rail</option>
        <option value="admin">Admin</option>
      </select>

      <input
        placeholder="Search email or name…"
        value={filters.search || ''}
        onChange={e => update({ search: e.target.value })}
        className={controlClass}
        style={{ flex: 1, minWidth: 200 }}
        aria-label="Search by email or display name"
      />

      {Object.keys(filters).length > 0 && (
        <button
          onClick={() => onChange({})}
          className="cursor-pointer rounded border bg-background px-3 py-1.5 text-sm hover:bg-muted"
          type="button"
        >
          Clear
        </button>
      )}
    </div>
  );
}

const controlClass =
  'rounded border bg-background px-2 py-1.5 text-sm text-foreground placeholder:text-muted-foreground';
